import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useAlert } from "../../context/AlertContext";
import { useTranscriptRequest } from "../../hooks/useTranscription";
import { useTranslation } from "react-i18next";
import type { RedactionOptions } from "../../api/transcription";
import RedactionOptionFields from "./RedactionOptionFields";

const emptyRedactionOptions: RedactionOptions = {
  enabled: false,
  piiToRedact: { financial: false, personal: false },
};

export interface TranscriptRequestDialogProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  customerId: string;
  interactionId: string;
  hasExistingTranscript?: boolean;
  onTranscriptionStarted?: (jobId: string) => void;
}

const TranscriptRequestDialog = ({
  isOpen,
  setIsOpen,
  customerId,
  interactionId,
  hasExistingTranscript = false,
  onTranscriptionStarted,
}: TranscriptRequestDialogProps) => {
  const { t } = useTranslation(["transcription"]);
  const [redactionOptions, setRedactionOptions] =
    useState<RedactionOptions>(emptyRedactionOptions);
  const showAlert = useAlert();
  const { mutateAsync: requestTranscript, isPending } = useTranscriptRequest();

  const handleClose = () => {
    setIsOpen(false);
    setRedactionOptions(emptyRedactionOptions);
  };

  const handleSubmit = async () => {
    try {
      const response = await requestTranscript({
        customerId,
        interactionId,
        redactionOptions,
      });

      const message = redactionOptions.enabled
        ? t("transcription:redactedTranscriptRequestSubmitted")
        : t("transcription:transcriptRequestSubmitted");

      showAlert(message, "success");

      if (onTranscriptionStarted && response.jobId) {
        onTranscriptionStarted(response.jobId);
      }

      handleClose();
    } catch (error: unknown) {
      console.error(error);
      if (error instanceof Error) {
        showAlert(error.message, "error");
      } else {
        showAlert(t("transcription:failedToRequestTranscript"), "error");
      }
    }
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("transcription:transcriptionOptions")}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {t("transcription:transcriptionOptionsDescription")}
        </Typography>

        <RedactionOptionFields
          options={redactionOptions}
          onChange={setRedactionOptions}
          disabled={isPending}
          variant={hasExistingTranscript ? "required" : "optional"}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isPending}>
          {t("common:cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isPending || (hasExistingTranscript && !redactionOptions.enabled)}
        >
          {isPending
            ? t("transcription:transcriptRequestInProgress")
            : t("transcription:requestTranscript")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TranscriptRequestDialog;
